import { isSignalNode, tacticKey, type AttributionCampaign, type CsSubgraph } from '@bloo-team/api-client'
import type { SignalSeverity } from './signal-model'
import type { Subgraph } from './signal-subgraphs'

/**
 * Storyline attribution (docs/signals.md §Storyline panel): the campaigns a
 * connected-signals subgraph matches (`models/signals_attribution`), their
 * likelihood bands, and the storyline's ATT&CK techniques by tactic with the
 * selected campaigns laid over them. Pure — the hooks live in signals.ts.
 */

export type Campaign = AttributionCampaign

/**
 * The request body's `graph_data`. The server's helper walks
 * `nodes[0 .. no_of_signals)` as if those were the signal nodes, but
 * `no_of_signals` counts merged raw signals and node order is not
 * guaranteed — so it reaches an entity node and the whole attribution
 * fails (BACKEND-ASKS CB-69). Signal nodes go first and the count is theirs.
 */
export function attributionPayload(raw: CsSubgraph): CsSubgraph {
  const signals = raw.nodes.filter(isSignalNode)
  const entities = raw.nodes.filter((n) => !isSignalNode(n))
  return { ...raw, nodes: [...signals, ...entities], no_of_signals: signals.length }
}

/** Likelihood bands on the severity scale: ≥ 85 · ≥ 65 · ≥ 45 · ≥ 25 · below. */
export function likelihoodBand(likelihood: number): SignalSeverity {
  if (likelihood >= 85) return 'critical'
  if (likelihood >= 65) return 'high'
  if (likelihood >= 45) return 'medium'
  if (likelihood >= 25) return 'low'
  return 'info'
}

export interface MitreTechnique {
  name: string
  /** Kept signals in the storyline with this tactic + technique; 0 = only a selected campaign names it. */
  signals: number
  /** Selected campaigns that explain it, highest likelihood first. */
  campaigns: string[]
  /** The highest of those campaigns' likelihoods; null when none explains it. */
  likelihood: number | null
}

export interface MitreRow {
  tactic: string
  techniques: MitreTechnique[]
}

/** The helper skips this technique; so does the matrix. */
const UNCLASSIFIED = 'unclassified'

/**
 * The storyline's techniques grouped by tactic (ATT&CK order, unknown
 * tactics last), each scored by the highest likelihood among the SELECTED
 * campaigns that list it under the same tactic. A technique only a selected
 * campaign names still gets a row entry (signals: 0) — live, the helper only
 * returns techniques the storyline holds, so that stays empty there.
 */
export function mitreRows(
  sg: Pick<Subgraph, 'signals'>,
  campaigns: readonly Campaign[],
  selected: ReadonlySet<string>,
  order: readonly string[],
): MitreRow[] {
  const rows = new Map<string, { tactic: string; techniques: Map<string, MitreTechnique> }>()
  const entry = (tactic: string, technique: string) => {
    const tk = tacticKey(tactic)
    let row = rows.get(tk)
    if (!row) rows.set(tk, (row = { tactic, techniques: new Map() }))
    let t = row.techniques.get(technique)
    if (!t) row.techniques.set(technique, (t = { name: technique, signals: 0, campaigns: [], likelihood: null }))
    return t
  }
  for (const s of sg.signals) {
    if (!s.tactic || !s.technique || s.technique.toLowerCase() === UNCLASSIFIED) continue
    entry(s.tactic, s.technique).signals++
  }
  const picked = campaigns.filter((c) => selected.has(c.name)).sort((a, b) => b.likelihood - a.likelihood)
  for (const c of picked) {
    for (const [tactic, techniques] of Object.entries(c.techniques)) {
      for (const technique of techniques) {
        if (!technique || technique.toLowerCase() === UNCLASSIFIED) continue
        const t = entry(tactic, technique)
        if (!t.campaigns.includes(c.name)) t.campaigns.push(c.name)
        t.likelihood = Math.max(t.likelihood ?? 0, c.likelihood)
      }
    }
  }
  const rank = (tactic: string) => {
    const i = order.findIndex((o) => tacticKey(o) === tacticKey(tactic))
    return i < 0 ? order.length : i
  }
  return [...rows.values()]
    .sort((a, b) => rank(a.tactic) - rank(b.tactic) || a.tactic.localeCompare(b.tactic))
    .map((r) => ({ tactic: r.tactic, techniques: [...r.techniques.values()].sort((a, b) => a.name.localeCompare(b.name)) }))
}

export interface MitreSummary {
  /** Every ATT&CK tactic in matrix order (Unclassified left out), with this storyline's techniques under it. */
  tactics: { tactic: string; techniques: MitreTechnique[]; signals: number }[]
  /** Tactics with at least one technique. */
  covered: number
  /** The most-signalled techniques (observed only), most first, then by name. */
  top: (MitreTechnique & { tactic: string })[]
  /** The largest signal count on one technique — the heat scale's top. */
  maxSignals: number
}

/**
 * The MITRE tab's summary and matrix columns: `mitreRows` laid onto the full
 * tactic order, so tactics the storyline never touched still get their
 * (empty) column. A row whose tactic the order does not know is appended.
 */
export function mitreSummary(rows: readonly MitreRow[], order: readonly string[], top = 5): MitreSummary {
  const byKey = new Map(rows.map((r) => [tacticKey(r.tactic), r]))
  const names = order.filter((o) => tacticKey(o) !== UNCLASSIFIED)
  for (const r of rows) if (!names.some((n) => tacticKey(n) === tacticKey(r.tactic))) names.push(r.tactic)
  const tactics = names.map((tactic) => {
    const techniques = byKey.get(tacticKey(tactic))?.techniques ?? []
    return { tactic, techniques, signals: techniques.reduce((n, t) => n + t.signals, 0) }
  })
  const all = tactics.flatMap((t) => t.techniques.map((x) => ({ ...x, tactic: t.tactic })))
  return {
    tactics,
    covered: tactics.filter((t) => t.techniques.length > 0).length,
    top: all
      .filter((t) => t.signals > 0)
      .sort((a, b) => b.signals - a.signals || a.name.localeCompare(b.name))
      .slice(0, top),
    maxSignals: all.reduce((m, t) => Math.max(m, t.signals), 0),
  }
}

/** A technique's heat step 0–4 against the storyline's busiest technique (0 = only a campaign names it). */
export function heatLevel(signals: number, max: number): 0 | 1 | 2 | 3 | 4 {
  if (signals <= 0 || max <= 0) return 0
  return Math.max(1, Math.min(4, Math.ceil((signals / max) * 4))) as 1 | 2 | 3 | 4
}

/* -------------------------------------------------------------- demo mode */

/**
 * Demo campaigns — FICTIONAL names over the demo fixture's techniques, so
 * the demo never attributes anything to a real actor.
 */
export const DEMO_CAMPAIGNS: readonly { name: string; techniques: readonly string[]; description: string }[] = [
  {
    name: 'Operation Paper Lantern',
    techniques: ['OS Credential Dumping', 'SMB/Windows Admin Shares', 'Command and Scripting Interpreter', 'Valid Accounts', 'Create or Modify System Process'],
    description:
      'Demo campaign. Hands-on-keyboard intrusion that dumps LSASS on a first foothold, then moves laterally over admin shares with the harvested accounts and plants a service for persistence.',
  },
  {
    name: 'Copper Ferret',
    techniques: ['Brute Force', 'Valid Accounts', 'External Remote Services', 'Network Service Discovery'],
    description: 'Demo campaign. Password spraying against exposed VPN and remote-access services, followed by internal service discovery from the accounts that worked.',
  },
  {
    name: 'Silent Ledger',
    techniques: ['Application Layer Protocol', 'DNS', 'Exfiltration Over C2 Channel', 'Scheduled Task/Job'],
    description: 'Demo campaign. Low-and-slow data theft: a scheduled task beacons over DNS and HTTPS and exfiltrates through the same channel.',
  },
  {
    name: 'Glass Harbor',
    techniques: ['Command and Scripting Interpreter', 'Scheduled Task/Job', 'OS Credential Dumping'],
    description: 'Demo campaign. Script-driven credential theft staged through scheduled tasks on file servers.',
  },
  {
    name: 'Night Weaver',
    techniques: ['Valid Accounts', 'SMB/Windows Admin Shares', 'Exfiltration Over C2 Channel'],
    description: 'Demo campaign. Abuses legitimate accounts to stage data on admin shares before exfiltrating it.',
  },
  {
    name: 'Amber Relay',
    techniques: ['External Remote Services', 'Application Layer Protocol'],
    description: 'Demo campaign. Uses a compromised remote-access appliance as a relay for command and control.',
  },
]

/**
 * The server's scoring over the demo campaigns: likelihood = the share of
 * the storyline's distinct (classified) techniques a campaign explains, two
 * decimals; techniques grouped by the storyline's tactic; campaigns that
 * explain none dropped; top 5, highest first.
 */
export function demoAttribution(sg: Pick<Subgraph, 'signals'>): Campaign[] {
  const tacticOf = new Map<string, string>()
  for (const s of sg.signals) if (s.technique && s.technique.toLowerCase() !== UNCLASSIFIED && s.tactic) tacticOf.set(s.technique, s.tactic)
  const total = tacticOf.size
  if (!total) return []
  const out: Campaign[] = []
  for (const c of DEMO_CAMPAIGNS) {
    const hit = c.techniques.filter((t) => tacticOf.has(t))
    if (!hit.length) continue
    const techniques: Record<string, string[]> = {}
    for (const t of hit) (techniques[tacticOf.get(t)!] ??= []).push(t)
    out.push({ name: c.name, likelihood: Math.round((hit.length * 10_000) / total) / 100, techniques, description: c.description })
  }
  return out.sort((a, b) => b.likelihood - a.likelihood || a.name.localeCompare(b.name)).slice(0, 5)
}
